export const CUSTOM_HEATMAP_X_COLUMN = 'custom_x_value';
export const CUSTOM_HEATMAP_Y_COLUMN = 'custom_y_value';
export const IMAGE_DENSITY_MARKER_LIMIT = 100;

export function isHeatmapPositionColumn(column) {
  return column === CUSTOM_HEATMAP_X_COLUMN || column === CUSTOM_HEATMAP_Y_COLUMN;
}

export function heatmapOverlayColumns({ temporary = false } = {}) {
  return [
    { key: CUSTOM_HEATMAP_X_COLUMN, label: CUSTOM_HEATMAP_X_COLUMN, temporary },
    { key: CUSTOM_HEATMAP_Y_COLUMN, label: CUSTOM_HEATMAP_Y_COLUMN, temporary },
  ];
}

export function heatmapPositionFieldOptions() {
  return [
    { value: CUSTOM_HEATMAP_X_COLUMN, label: 'Posición X' },
    { value: CUSTOM_HEATMAP_Y_COLUMN, label: 'Posición Y' },
  ];
}

export function withHeatmapPositionFieldOptions(options, enabled) {
  if (!enabled) return options || [];
  const next = [...(options || [])];
  const seen = new Set(next.map((o) => o.value));
  for (const extra of heatmapPositionFieldOptions()) {
    if (!seen.has(extra.value)) next.push(extra);
  }
  return next;
}

export function appendHeatmapPositionVisibleColumns(columns) {
  const cols = Array.isArray(columns) ? [...columns] : [];
  for (const extra of [CUSTOM_HEATMAP_X_COLUMN, CUSTOM_HEATMAP_Y_COLUMN]) {
    if (!cols.includes(extra)) cols.push(extra);
  }
  return cols;
}

export function formatHeatmapPositionCoord(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Elige la columna identificador que mejor casa las claves de posiciones manuales.
 * Si la preferida ya acierta alguna fila, se conserva.
 */
export function inferPositionKeyColumn(data, positions, preferred = '') {
  const placed = normalizeManualPositions(positions);
  const placedKeys = new Set(Object.keys(placed));
  if (!placedKeys.size) return preferred || '';
  const rows = (Array.isArray(data) ? data : []).filter((row) => row && typeof row === 'object');
  if (!rows.length) return preferred || '';

  const scoreColumn = (col) => {
    if (!col || isHeatmapPositionColumn(col)) return 0;
    let hits = 0;
    const seen = new Set();
    for (const row of rows) {
      const key = positionKeyOfRow(row, col);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      if (placedKeys.has(key)) hits += 1;
    }
    return hits;
  };

  if (scoreColumn(preferred) > 0) return preferred;

  const candidates = new Set();
  for (const row of rows) {
    Object.keys(row).forEach((col) => {
      if (!isHeatmapPositionColumn(col)) candidates.add(col);
    });
  }
  let best = '';
  let bestScore = 0;
  for (const col of candidates) {
    const score = scoreColumn(col);
    if (score > bestScore) {
      best = col;
      bestScore = score;
    }
  }
  return bestScore > 0 ? best : preferred || '';
}

/** Añade custom_x_value / custom_y_value a cada fila según el mapa de posiciones. */
export function attachHeatmapPositionColumns(data, { positionKeyColumn, positions } = {}) {
  const placedMap = normalizeManualPositions(positions);
  return (data || []).map((row) => {
    if (!row || typeof row !== 'object') return row;
    const key = positionKeyOfRow(row, positionKeyColumn);
    const pos = key ? placedMap[key] : null;
    const placed = Number.isFinite(pos?.x) && Number.isFinite(pos?.y);
    return {
      ...row,
      [CUSTOM_HEATMAP_X_COLUMN]: placed ? pos.x : null,
      [CUSTOM_HEATMAP_Y_COLUMN]: placed ? pos.y : null,
    };
  });
}

/** Convierte un valor de columna (texto, número u objeto JSON) en clave estable y legible. */
export function formatPositionKeyValue(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  if (Array.isArray(value)) {
    return value.map(formatPositionKeyValue).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    const preferred = ['nombre', 'NOMBRE', 'name', 'label', 'title', 'id', 'ID', 'value', 'key'];
    for (const k of preferred) {
      if (!Object.prototype.hasOwnProperty.call(value, k)) continue;
      const nested = value[k];
      if (nested == null || nested === '' || typeof nested === 'object') continue;
      return String(nested);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
}

/** Clave de fila para unir datos con posiciones manuales. */
export function positionKeyOfRow(row, positionKeyColumn) {
  if (!row || !positionKeyColumn) return '';
  let value;
  if (Object.prototype.hasOwnProperty.call(row, positionKeyColumn)) {
    value = row[positionKeyColumn];
  } else {
    const lower = String(positionKeyColumn).toLowerCase();
    const key = Object.keys(row).find((k) => k.toLowerCase() === lower);
    if (!key) return '';
    value = row[key];
  }
  return formatPositionKeyValue(value);
}

/** Normaliza el mapa { clave: { x, y } } a coordenadas numéricas. */
export function normalizeManualPositions(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [key, pos] of Object.entries(raw)) {
    if (key == null || key === '') continue;
    const x = Number(pos?.x);
    const y = Number(pos?.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    out[String(key)] = { x, y };
  }
  return out;
}

function pointWeight(row, weightColumn) {
  if (!weightColumn) return 1;
  const w = Number(row[weightColumn]);
  return Number.isFinite(w) && w > 0 ? w : 1;
}

function pointGroup(row, groupByColumn) {
  if (!groupByColumn || row[groupByColumn] == null || row[groupByColumn] === '') return '';
  return String(row[groupByColumn]);
}

function buildPoint(row, rowIndex, x, y, { weightColumn, groupByColumn, positionKey }) {
  return {
    x,
    y,
    weight: pointWeight(row, weightColumn),
    group: pointGroup(row, groupByColumn),
    row,
    rowIndex,
    positionKey: positionKey || '',
    label: positionKey || '',
  };
}

/**
 * Parsea filas SQL en puntos del heatmap sobre imagen, conservando la fila origen para tooltips.
 * En mode manual, x/y salen de manualPositions[clave] en vez de columnas.
 */
export function parseImageDensityPoints(
  data,
  {
    xColumn,
    yColumn,
    weightColumn,
    groupByColumn,
    positionMode = 'columns',
    positionKeyColumn = '',
    manualPositions = {},
  } = {}
) {
  const isManual = positionMode === 'manual';
  const placed = isManual ? normalizeManualPositions(manualPositions) : null;
  const out = [];
  for (let rowIndex = 0; rowIndex < (data || []).length; rowIndex += 1) {
    const row = data[rowIndex];
    if (!row || typeof row !== 'object') continue;
    const positionKey = positionKeyOfRow(row, positionKeyColumn);
    let x;
    let y;
    if (isManual) {
      if (!positionKey) continue;
      const pos = placed[positionKey];
      x = Number(pos?.x);
      y = Number(pos?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    } else {
      x = Number(row[xColumn]);
      y = Number(row[yColumn]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    }
    out.push(
      buildPoint(row, rowIndex, x, y, { weightColumn, groupByColumn, positionKey })
    );
  }
  return out;
}

/** Entidades únicas por clave: colocadas vs pendientes de posicionar. */
export function listImageDensityPositionEntities(
  data,
  { positionKeyColumn, manualPositions } = {}
) {
  const placedMap = normalizeManualPositions(manualPositions);
  const seen = new Set();
  const all = [];
  const unplaced = [];
  for (const row of data || []) {
    if (!row || typeof row !== 'object') continue;
    const key = positionKeyOfRow(row, positionKeyColumn);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const pos = placedMap[key];
    const placed = Number.isFinite(pos?.x) && Number.isFinite(pos?.y);
    const entity = {
      key,
      label: key.length > 48 ? `${key.slice(0, 45)}…` : key,
      placed,
      x: pos?.x,
      y: pos?.y,
      row: {
        ...row,
        [CUSTOM_HEATMAP_X_COLUMN]: placed ? pos.x : null,
        [CUSTOM_HEATMAP_Y_COLUMN]: placed ? pos.y : null,
      },
    };
    all.push(entity);
    if (!placed) unplaced.push(entity);
  }
  const byLabel = [...all].sort((a, b) =>
    String(a.label || a.key).localeCompare(String(b.label || b.key), 'es', { numeric: true })
  );
  byLabel.forEach((entity, index) => {
    entity.markerNumber = index + 1;
  });
  return { all, unplaced, placed: placedMap };
}

const SKIP_COLUMN_RE = /^(background|image|img|foto|photo|url|thumbnail)$/i;

function columnLooksUsable(key, value) {
  if (!key || SKIP_COLUMN_RE.test(key)) return false;
  if (typeof value === 'string' && (value.startsWith('data:') || value.length > 180)) return false;
  return true;
}

/** Columnas compactas para la tabla de posicionamiento (identificador + métricas/datos). */
export function imageDensityPositionTableColumns(
  entities,
  { positionKeyColumn, tooltipColumns, metricColumns, max = 6 } = {}
) {
  const sample = (entities || []).find((e) => e?.row && typeof e.row === 'object')?.row || {};
  const keys = Object.keys(sample);
  const ordered = [];
  const add = (col) => {
    if (!col || ordered.includes(col) || !Object.prototype.hasOwnProperty.call(sample, col)) return;
    if (!columnLooksUsable(col, sample[col])) return;
    ordered.push(col);
  };
  add(positionKeyColumn);
  for (const col of metricColumns || []) add(col);
  for (const col of tooltipColumns || []) add(col);
  for (const col of keys) {
    if (ordered.length >= max) break;
    add(col);
  }
  const cols = ordered.slice(0, max);
  for (const extra of [CUSTOM_HEATMAP_X_COLUMN, CUSTOM_HEATMAP_Y_COLUMN]) {
    if (!cols.includes(extra)) cols.push(extra);
  }
  return cols;
}

/** Asegura count/members en un punto (individual o cluster). */
export function withPointMembers(point) {
  if (!point) return point;
  if (Array.isArray(point.members) && point.members.length) {
    return {
      ...point,
      count: point.count != null ? point.count : point.members.length,
    };
  }
  return { ...point, count: 1, members: [point] };
}

export const MARKER_VALUE_AGG_OPTIONS = [
  { value: 'first', label: 'Primer valor' },
  { value: 'sum', label: 'Suma' },
  { value: 'avg', label: 'Promedio' },
  { value: 'min', label: 'Mínimo' },
  { value: 'max', label: 'Máximo' },
  { value: 'count', label: 'Conteo' },
];

const MARKER_VALUE_AGG_SET = new Set(MARKER_VALUE_AGG_OPTIONS.map((o) => o.value));

export function normalizeMarkerValueAggregation(value) {
  const key = String(value || '').trim();
  return MARKER_VALUE_AGG_SET.has(key) ? key : 'first';
}

function rowCell(row, column) {
  if (!row || !column) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, column)) return row[column];
  const lower = String(column).toLowerCase();
  const key = Object.keys(row).find((k) => k.toLowerCase() === lower);
  return key ? row[key] : undefined;
}

function formatAggregatedNumber(n) {
  if (!Number.isFinite(n)) return '';
  if (Number.isInteger(n)) return String(n);
  const abs = Math.abs(n);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return n.toFixed(digits).replace(/\.?0+$/, '');
}

/**
 * Valor a mostrar dentro del marcador a partir de todas las filas del punto
 * (misma cámara/posición o cluster).
 */
export function aggregateMarkerValue(point, column, aggregation = 'first') {
  const agg = normalizeMarkerValueAggregation(aggregation);
  const members = withPointMembers(point).members || [];
  if (agg === 'count') {
    return { text: String(members.length), numeric: members.length };
  }
  if (!column || !members.length) return { text: '', numeric: null };

  const values = members.map((m) => rowCell(m?.row, column));
  if (agg === 'first') {
    const first = values.find((v) => v != null && String(v).trim() !== '');
    if (first == null) return { text: '', numeric: null };
    const n = Number(first);
    return {
      text: String(first),
      numeric: Number.isFinite(n) ? n : null,
    };
  }

  const nums = values.map((v) => Number(v)).filter((n) => Number.isFinite(n));
  if (!nums.length) {
    const first = values.find((v) => v != null && String(v).trim() !== '');
    return { text: first != null ? String(first) : '', numeric: null };
  }
  let numeric;
  if (agg === 'sum') numeric = nums.reduce((a, b) => a + b, 0);
  else if (agg === 'avg') numeric = nums.reduce((a, b) => a + b, 0) / nums.length;
  else if (agg === 'min') numeric = Math.min(...nums);
  else if (agg === 'max') numeric = Math.max(...nums);
  else numeric = nums[0];
  return { text: formatAggregatedNumber(numeric), numeric };
}

function markerCoalesceKey(point) {
  if (point?.positionKey) return `k:${point.positionKey}`;
  return `xy:${point?.x}:${point?.y}:${point?.group || ''}`;
}

/**
 * Un marcador por entidad (misma posición/clave): junta filas (p. ej. una por clase)
 * para poder agregar el valor mostrado.
 */
export function coalescePointsForMarkers(points) {
  if (!points?.length) return [];
  const buckets = new Map();
  for (const p of points) {
    if (!p) continue;
    const key = markerCoalesceKey(p);
    if (!buckets.has(key)) {
      buckets.set(key, {
        ...p,
        members: [],
      });
    }
    const bucket = buckets.get(key);
    const parts = withPointMembers(p).members || [p];
    for (const m of parts) bucket.members.push(m);
  }
  return [...buckets.values()].map((bucket) => {
    const members = bucket.members;
    const n = members.length || 1;
    let sumX = 0;
    let sumY = 0;
    let sumWeight = 0;
    for (const m of members) {
      sumX += Number(m.x) || 0;
      sumY += Number(m.y) || 0;
      sumWeight += m.weight != null && Number.isFinite(m.weight) ? m.weight : 1;
    }
    const first = members[0] || bucket;
    return {
      ...bucket,
      x: sumX / n,
      y: sumY / n,
      weight: sumWeight,
      count: n,
      members,
      row: first.row,
      rowIndex: first.rowIndex,
      positionKey: bucket.positionKey || first.positionKey || '',
      label: bucket.label || first.label || '',
      group: bucket.group || first.group || '',
      markerNumber: bucket.markerNumber ?? first.markerNumber,
    };
  });
}
